import { useMemo, useState } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Filter } from "lucide-react";
import {
  OrderEventType,
  TtOrderEvent,
  TtOrderMetadata,
} from "@/lib/api/risk/brokerage/tt";
import { CommonDestinationTimezone, cn } from "@/lib/utils";
import { ContractPreviewButton } from "./ContractPreviewButton";

interface OrderEventsSectionProps {
  events: TtOrderEvent[];
  metadata: TtOrderMetadata | null;
  guidOrderId: string;
}

const EVENT_TYPE_BADGE: Record<string, string> = {
  Filled: "bg-green-100 text-green-800 border-green-200",
  Added: "bg-blue-100 text-blue-800 border-blue-200",
  Updated: "bg-slate-100 text-slate-700 border-slate-200",
  Deleted: "bg-zinc-100 text-zinc-700 border-zinc-200",
  Rejected: "bg-red-100 text-red-800 border-red-200",
  StatusUnknown: "bg-amber-100 text-amber-800 border-amber-200",
  Timeout: "bg-amber-100 text-amber-800 border-amber-200",
  PendingAction: "bg-amber-100 text-amber-800 border-amber-200",
};

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return moment.tz(iso, CommonDestinationTimezone).format("MM/DD HH:mm:ss");
}

function Stat({ label, value }: { label: string; value: string | null | undefined }) {
  if (value == null || value === "") return null;
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-xs font-medium tabular-nums">{value}</span>
    </span>
  );
}

function EventCard({
  event,
  metadata,
  guidOrderId,
}: {
  event: TtOrderEvent;
  metadata: TtOrderMetadata | null;
  guidOrderId: string;
}) {
  const badgeClass = EVENT_TYPE_BADGE[event.event_type ?? ""] ?? "bg-muted text-muted-foreground";
  return (
    <div className="border rounded-md px-2 py-1.5 flex flex-col gap-1 bg-background hover:bg-muted/30">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className={cn("text-[10px] py-0 px-1.5", badgeClass)}>
            {event.event_type ?? "Unknown"}
          </Badge>
          <span className="text-xs text-muted-foreground tabular-nums">
            {formatTime(event.event_time)}
          </span>
          {event.buy_sell_type && (
            <span className="text-xs font-semibold">{event.buy_sell_type}</span>
          )}
        </div>
        <ContractPreviewButton event={event} metadata={metadata} guidOrderId={guidOrderId} />
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        <Stat label="Order" value={event.order_quantity} />
        <Stat label="Filled" value={event.filled_quantity} />
        <Stat label="Working" value={event.working_quantity} />
        <Stat label="Limit" value={event.limit_price} />
        <Stat label="Avg Fill" value={event.avg_fill_price} />
      </div>

      {(event.message || event.reject_message) && (
        <div className="text-xs text-muted-foreground break-words">
          {event.reject_message && (
            <div className="text-red-600">{event.reject_message}</div>
          )}
          {event.message && <div>{event.message}</div>}
        </div>
      )}
    </div>
  );
}

export function OrderEventsSection({ events, metadata, guidOrderId }: OrderEventsSectionProps) {
  const [hideEmptyUpdates, setHideEmptyUpdates] = useState(true);

  const filteredEvents = useMemo(() => {
    if (!hideEmptyUpdates) return events;
    return events.filter((event) => {
      if (event.event_type !== OrderEventType.Updated) return true;
      const qty = event.updated_working_quantity;
      return qty !== null && qty !== "0" && qty !== "";
    });
  }, [events, hideEmptyUpdates]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <span className="text-xs text-muted-foreground">{filteredEvents.length} events</span>
        <Button
          variant={hideEmptyUpdates ? "default" : "outline"}
          size="sm"
          onClick={() => setHideEmptyUpdates(!hideEmptyUpdates)}
        >
          <Filter className="h-3 w-3 mr-1" />
          {hideEmptyUpdates ? "Show All Updates" : "Hide Empty Updates"}
        </Button>
      </div>

      {filteredEvents.length === 0 ? (
        <p className="text-xs text-muted-foreground">No events.</p>
      ) : (
        <div className="flex flex-col gap-1.5 max-h-[360px] overflow-y-auto pr-1">
          {filteredEvents.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              metadata={metadata}
              guidOrderId={guidOrderId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
